/*
 * Renders a subsection on a TaxSummary's section
 */

import React from "react";
import { Select, SelectItem } from "carbon-components-react";
import { AppShape, DocTypes, TaxIncomeSection, TaxIncomeSubsection } from "@eave/shared/types/api";
import Money from "web_modules/Money";
import EditableField from "underwriter_dashboard/shared/EditableField";
import {
  borrowerEntityToDisplay,
  getAlternateAggregationKeys,
  getCompositeKey,
  renderFieldTextCell,
  renderOperationSymbol,
  reoIDToDisplayAddress,
} from "../../helpers";
import {
  CALCULATION_METHOD_OPTIONS,
  ModifiedAggregationMap,
  taxReturnYearOptions,
} from "../../shared";

interface Props {
  app: AppShape;
  section?: TaxIncomeSection;
  subsection: TaxIncomeSubsection;
  qualificationYear: number;
  modifiedAggregations: ModifiedAggregationMap;
  setModifiedAggregations: React.Dispatch<React.SetStateAction<ModifiedAggregationMap>>;
}

const TaxSummarySubsection: React.FC<Props> = ({
  app,
  section,
  subsection,
  qualificationYear,
  modifiedAggregations,
  setModifiedAggregations,
}: Props) => {
  const handleAggregationChange = (propsAggregationRule, field) => {
    const newState: ModifiedAggregationMap = { ...modifiedAggregations };

    const compositeKey = getCompositeKey(propsAggregationRule);
    let modifiedAggregation = newState[compositeKey];
    if (modifiedAggregation === undefined) {
      // save a copy
      modifiedAggregation = JSON.parse(JSON.stringify(propsAggregationRule));
      newState[compositeKey] = modifiedAggregation;
    }

    // decimal/nulldecimal fields cannot be empty, convert to null
    const nullDecimalFields = ["gross_up_override", "other_year_1", "other_year_2"];
    const isEmptyDecimal = nullDecimalFields.includes(field.name) && field.value === "";

    if (field.value === null || isEmptyDecimal) {
      // null field value means a reset
      modifiedAggregation[field.name] = propsAggregationRule[field.name];
    } else if (
      field.name === "gross_up_override" &&
      (Number(field.value) === 0 || Number(field.value) > 50)
    ) {
      // TODO add validation/constraint of max 50% while editing, reset for now
      // zero means remove override for gross up
      modifiedAggregation[field.name] = null;
    } else {
      // the value can be false for checkbox
      modifiedAggregation[field.name] = field.value;
    }

    setModifiedAggregations(newState);
  };

  const setSubsectionValue = (propsAggregationRule, fieldName, value) => {
    const field = {
      name: fieldName,
      value,
    };
    handleAggregationChange(propsAggregationRule, field);
  };

  const setAlternateAggregationsValues = (
    alternateAggregationKeys,
    taxSummarySection: TaxIncomeSection,
    subKey,
    name,
    value,
  ) => {
    alternateAggregationKeys.forEach(key => {
      const targetCompositeKey = `${key}::${subKey}`;

      // if there's a subsection for this composite key, set its value
      const targetSubsections = taxSummarySection.subsections.filter(
        ss => getCompositeKey(ss.aggregation) === targetCompositeKey,
      );

      if (targetSubsections.length === 0) {
        return;
      }

      const targetAggregation = targetSubsections[0].aggregation;
      setSubsectionValue(targetAggregation, name, value);
    });
  };

  const toggleSubsectionValue = (propsAggregationRule, fieldName) => {
    const compositeKey = getCompositeKey(propsAggregationRule);
    const aggregation = modifiedAggregations[compositeKey] || propsAggregationRule;
    const value = !aggregation[fieldName];
    setSubsectionValue(propsAggregationRule, fieldName, value);
    return value;
  };

  const handleIncludeInRatios = (aggregation, taxSummarySection, ev) => {
    const includingInRatios = toggleSubsectionValue(aggregation, ev.target.name);

    // if including in ratios, then alternates (eg a lease vs a schedule E, for REO) should not be included.
    // however, the opposite isn't necessarily true.
    // if Ops is marking the current aggregation as not include in ratios, don't mark the alternate as included,
    // since then Ops would have no way of not including the income entirely (from any source)
    if (!includingInRatios) {
      return;
    }

    const alternateAggregationKeys = getAlternateAggregationKeys(aggregation);
    if (alternateAggregationKeys !== null) {
      setAlternateAggregationsValues(
        alternateAggregationKeys,
        taxSummarySection,
        aggregation.sub_key,
        ev.target.name,
        !includingInRatios,
      );
    }
  };
  const hasSingleSubsection = section ? section.subsections.length === 1 : false;

  const renderAggregationValueElement = (fieldAggregation, value) => {
    if (fieldAggregation.type === "money") {
      return <Money value={value} />;
    }
    if (fieldAggregation.type === "number") {
      return value;
    }
    return fieldAggregation.fixed_value;
  };

  const compositeKey = getCompositeKey(subsection.aggregation);
  const aggregationToUse = modifiedAggregations[compositeKey] || subsection.aggregation;

  const grossUp = subsection.aggregation.show_gross_up;
  const grossedUpText = grossUp ? " (grossed up)" : "";
  const showOther = subsection.aggregation.show_other;
  const isProfitAndLoss = subsection.aggregation.document_type === DocTypes.ProfitAndLossStatement;
  const isREOLease = subsection.aggregation.document_type === "real_estate_lease";
  const isTaxReturn = subsection.aggregation.document_type === "tax_return";
  const hasSubKey = subsection.aggregation.sub_key !== "";
  const subKeyField = subsection.aggregation.sub_key_field;
  const subKeys = subsection.aggregation.sub_key.split("::");
  const borrowerEntity = subsection.aggregation.borrower_entity;

  const showAggregation = renderAggregationValueElement;

  const taxReturnSubsectionKeyToHeading = {
    rental_real_estate: "Schedule E (Form 1040)",
  };

  return (
    <tbody key={compositeKey}>
      {isProfitAndLoss && (
        <tr key={`${compositeKey}_pandl`}>
          <td className="tax-return__profit-loss" colSpan={3}>
            <h4>
              Year-To-Date Profit and Loss for
              {borrowerEntityToDisplay(borrowerEntity)}; EIN {subKeys[1] || "(not ingested)"}
            </h4>
          </td>
        </tr>
      )}
      {isREOLease && (
        <tr key={`${compositeKey}_reo`}>
          <td className="tax-return__reo-lease" colSpan={3}>
            <h4>Rental Lease</h4>
            Only allowed if borrower has owned property at least 6 months
            <br />
            Property: {reoIDToDisplayAddress(app.owned_real_estate, +subKeys[0])}
          </td>
        </tr>
      )}
      {isTaxReturn && (
        <tr key={`${compositeKey}_taxReturnHeading`}>
          <td className="tax-return__profit-loss">
            {taxReturnSubsectionKeyToHeading[subsection.aggregation.key] && (
              <h4>{taxReturnSubsectionKeyToHeading[subsection.aggregation.key]}</h4>
            )}
            {hasSubKey && subKeyField === "ein" && <h4>EIN {subKeys[0] || "(not ingested)"}</h4>}
            {hasSubKey && subKeyField === "borrower_entity" && (
              <h4>{borrowerEntityToDisplay(borrowerEntity)}</h4>
            )}
            {hasSubKey && subKeyField === "borrower_entity::ein" && (
              <h4>
                {borrowerEntityToDisplay(borrowerEntity)}; EIN {subKeys[1] || "(not ingested)"}
              </h4>
            )}
          </td>
          <td>
            <Select
              id={`${compositeKey}_taxReturnYearsChooser`}
              defaultValue={aggregationToUse.year_1}
              onChange={e => {
                const field = {
                  name: "year_1_override",
                  value: parseInt(e.target.value, 10),
                };
                handleAggregationChange(aggregationToUse, field);
              }}
              labelText=""
              inline
              light
            >
              {taxReturnYearOptions(qualificationYear, aggregationToUse.year_1).map(o => (
                <SelectItem
                  key={`${compositeKey}_taxReturnYearsChooser_${o.value}`}
                  text={o.text}
                  value={o.value}
                />
              ))}
            </Select>
          </td>

          {/* to match SelectItem styles */}
          <td style={{ fontSize: "0.875rem", color: "#161616", verticalAlign: "middle" }}>
            {aggregationToUse.year_2}
          </td>
        </tr>
      )}
      {subsection.field_aggregations &&
        subsection.field_aggregations.map((fieldAggregation, i) => {
          if (fieldAggregation.display_text === "") {
            return null;
          }
          if (isProfitAndLoss) {
            return (
              <tr key={`${compositeKey}_${i}`}>
                {renderFieldTextCell(fieldAggregation)}
                <td />
                <td>{showAggregation(fieldAggregation, fieldAggregation.ytd_aggregated_value)}</td>
              </tr>
            );
          }
          if (isREOLease) {
            return (
              <tr key={`${compositeKey}_${i}`}>
                {renderFieldTextCell(fieldAggregation)}
                <td>
                  {showAggregation(fieldAggregation, fieldAggregation.monthly_aggregated_value)}
                </td>
              </tr>
            );
          }
          return (
            <tr key={`${compositeKey}_${i}`}>
              {renderFieldTextCell(fieldAggregation)}
              <td>
                {showAggregation(
                  fieldAggregation,
                  fieldAggregation.year_minus_one_aggregated_value,
                )}
              </td>
              <td>
                {showAggregation(
                  fieldAggregation,
                  fieldAggregation.year_minus_two_aggregated_value,
                )}
              </td>
            </tr>
          );
        })}
      {showOther && (
        <tr key={`${compositeKey}_other`}>
          <td>Other</td>
          <td>
            $
            <EditableField
              fieldName="other_year_2"
              inputName={`other_year_2_${compositeKey}`}
              inputType="number"
              originalValue={subsection.aggregation.other_year_2 || 0}
              changedValue={aggregationToUse.other_year_2 || 0}
              handleOnChange={handleAggregationChange.bind(this, subsection.aggregation)}
            />
          </td>
          <td>
            $
            <EditableField
              fieldName="other_year_1"
              inputName={`other_year_1_${compositeKey}`}
              inputType="number"
              originalValue={subsection.aggregation.other_year_1 || 0}
              changedValue={aggregationToUse.other_year_1 || 0}
              handleOnChange={handleAggregationChange.bind(this, subsection.aggregation)}
            />
          </td>
        </tr>
      )}
      {subsection.override_values.map(v => (
        <tr key={v.name}>
          <td>
            {v.name} {renderOperationSymbol(v.operation)}
          </td>
          <td>
            <Money value={v.value} />
          </td>
        </tr>
      ))}
      <tr key={`${compositeKey}_notes`}>
        <td>
          Notes:&nbsp;
          <EditableField
            fieldName="notes"
            inputName={`notes_${compositeKey}`}
            inputType="textarea"
            originalValue={subsection.aggregation.notes || ""}
            changedValue={aggregationToUse.notes || ""}
            handleOnChange={handleAggregationChange.bind(this, subsection.aggregation)}
          />
        </td>
        <td />
        <td>
          {grossUp && (
            <div>
              Gross up by&nbsp;
              <EditableField
                fieldName="gross_up_override"
                inputName={`gross_up_override_${compositeKey}`}
                inputType="number"
                originalValue={
                  subsection.aggregation.gross_up_override ||
                  subsection.calculated_gross_up_percent ||
                  0
                }
                changedValue={
                  aggregationToUse.gross_up_override || subsection.calculated_gross_up_percent || 0
                }
                handleOnChange={handleAggregationChange.bind(this, subsection.aggregation)}
              />
              %
            </div>
          )}
        </td>
      </tr>
      <tr key={`${compositeKey}_total`}>
        {isProfitAndLoss && <td>Divided by Total Months in P&amp;L</td>}
        {isTaxReturn && (
          <td>
            <EditableField
              inputType="select"
              inputName={`calculation_method_${compositeKey}`}
              fieldName="calculation_method"
              originalValue={subsection.aggregation.calculation_method}
              changedValue={aggregationToUse.calculation_method}
              options={CALCULATION_METHOD_OPTIONS}
              handleOnChange={handleAggregationChange.bind(this, subsection.aggregation)}
            />
          </td>
        )}
        <td>
          {!hasSingleSubsection && (
            <span>
              Monthly subtotal: <Money value={subsection.monthly_total} />
              {grossedUpText}
            </span>
          )}
        </td>
        <td>
          <input
            name="include_in_ratios"
            type="checkbox"
            onChange={handleIncludeInRatios.bind(this, subsection.aggregation, section)}
            checked={aggregationToUse.include_in_ratios}
          />
          <label htmlFor="include_in_ratios">Include in ratios</label>
        </td>
      </tr>
    </tbody>
  );
};

export default TaxSummarySubsection;
